import { SUPER_ADMIN_WILDCARD } from './types'

import { navigationConfig } from '@/config/navigation'
import type { NavigationItem } from '@/types/layout'

interface PathRule {
  path: string
  requiredPermissions: string[]
}

const flatten = (items: NavigationItem[], acc: PathRule[]): PathRule[] => {
  for (const item of items) {
    if (item.path && item.requiredPermissions && item.requiredPermissions.length > 0) {
      acc.push({ path: item.path, requiredPermissions: item.requiredPermissions })
    }
    if (item.children && item.children.length > 0) flatten(item.children, acc)
  }
  return acc
}

const rules: PathRule[] = flatten(navigationConfig.items, []).sort(
  (a, b) => b.path.length - a.path.length,
)

/**
 * Find the required permissions (OR-list) for the given pathname by
 * longest-prefix match against the sidebar navigation config.
 * Returns `null` when no rule applies — such paths are unrestricted
 * (e.g. `/login`, `/403`, static helpers).
 */
export const getRequiredPermissionsForPath = (pathname: string): string[] | null => {
  for (const rule of rules) {
    if (pathname === rule.path || pathname.startsWith(rule.path + '/')) {
      return rule.requiredPermissions
    }
  }
  return null
}

/**
 * Walk the sidebar in order and return the first leaf path the given
 * permissions grant access to. Used to land the user on a usable page
 * after login when they lack dashboard access.
 */
export const findFirstAllowedPath = (
  permissions: string[],
  isSuperAdmin: boolean,
): string | null => {
  if (isSuperAdmin || permissions.includes(SUPER_ADMIN_WILDCARD)) {
    return '/dashboard'
  }
  const held = new Set(permissions)
  const walk = (items: NavigationItem[]): string | null => {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        const found = walk(item.children)
        if (found) return found
        continue
      }
      if (!item.path) continue
      const required = item.requiredPermissions ?? []
      if (required.length === 0) return item.path
      if (required.some((code) => held.has(code))) return item.path
    }
    return null
  }
  return walk(navigationConfig.items)
}
